import { DiscountRuleResponse } from 'components/offers/DiscountRule/types';

export interface Offer {
  id: number;
  shopId: number;
  title: string;
  description: string;
  discountCode: string | null;
  discountRule: DiscountRuleResponse | null;
  discountValue: string | null;
  offerType: OfferType;
  status: OfferStatus;
  isDraft: boolean;
  isSitewideSale: boolean;
  isStackableWithEvergreen: boolean;
  source: OfferSource;
  startsAt: string | null;
  endsAt: string | null;
  promoteFrom: string | null;
  createdAt: string;
  updatedAt: string;
  discountRuleId: number | null;
  segmentId?: number | null;
  segment: {
    id: number;
    name: string;
    totalCustomersSendable: number | null;
  } | null;
  isFromOms?: boolean;
  offerUuid?: string | null;
}

export enum OfferSource {
  SHOPIFY = 'shopify',
  SHOPIFY_SYNC = 'shopify_sync',
  CASHBACK = 'cashback',
}

export enum OfferCategory {
  SITEWIDE_SALE = 'sitewide_sale',
  STANDARD = 'standard',
}

export enum OfferType {
  DISCOUNT_CODE = 'discount_code',
  AUTOMATIC_DISCOUNT = 'automatic_discount',
  UNIQUE_DISCOUNT_CODE = 'unique_discount_code',
}

export const OfferStrategy = {
  SITE_WIDE_SALE: 'site-wide-sale',
  EVERGREEN: 'evergreen',
} as const;
export type OfferStrategy = typeof OfferStrategy[keyof typeof OfferStrategy];

export enum OfferStatus {
  SCHEDULED = 'scheduled',
  PRE_PROMOTION = 'pre_promotion',
  ACTIVE = 'active',
  ENDED = 'ended',
  DRAFT = 'draft',
}

export interface OffersPageInfo {
  page: number;
  totalPages: number;
  totalCount: number;
}

export interface OffersResponse {
  offers: Offer[];
  pageInfo?: OffersPageInfo;
}

export interface OffersQueryParams {
  page?: number;
  isSitewideSale?: boolean;
}

export interface OfferStatusCounts {
  draft: number;
  scheduled: number;
  prePromotion: number;
  active: number;
  ended: number;
}

export interface OfferCountsResponse {
  strategic: OfferStatusCounts;
  onsite: OfferStatusCounts;
}

export interface TriggerOfferScanResponse {
  workflowId: string;
  runId?: string | null;
}

export const OfferScanLifecycleStatus = {
  RUNNING: 'running',
  COMPLETED: 'completed',
  PARTIAL: 'partial',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
} as const;

export type OfferScanLifecycleStatus =
  typeof OfferScanLifecycleStatus[keyof typeof OfferScanLifecycleStatus];

export interface OfferScanPageOutcomeCounts {
  succeeded: number;
  noOffers: number;
  blocked: number;
  failed: number;
}

export interface OfferScanArtifactReference {
  schemaVersion: number;
  objectKey: string;
  contentHash: string;
}

export interface OfferScanStatusResponse {
  schemaVersion: number;
  workflowId: string;
  runId?: string | null;
  lifecycleStatus: OfferScanLifecycleStatus;
  progressAvailable: boolean;
  phase?: string | null;
  completedPageCount?: number | null;
  totalPageCount?: number | null;
  pageOutcomeCounts?: OfferScanPageOutcomeCounts | null;
  discoveredUrlCount?: number | null;
  selectedUrlCount?: number | null;
  completionReason?: string | null;
  finalManifestRef?: OfferScanArtifactReference | null;
}

export interface CreateOfferRequest {
  title: string;
  description: string;
  discountCode?: string | null;
  discountValue?: string | null;
  offerType: string;
  source: OfferSource;
  isDraft: boolean;
  isStackableWithEvergreen: boolean;
  isSitewideSale: boolean;
  startsAt?: string | null;
  endsAt?: string | null;
  promoteFrom?: string | null;
  discountRuleId?: number | null;
  segmentId?: number | null;
  manualOfferKind?: ManualOfferKind;
  discountAmount?: string;
}

export interface CreateOfferResponse {
  offer: Offer;
}

export interface UpdateOfferRequest {
  title?: string;
  description?: string;
  discountCode?: string | null;
  discountValue?: string | null;
  offerType?: string;
  isDraft?: boolean;
  isStackableWithEvergreen?: boolean;
  startsAt?: string | null;
  endsAt?: string | null;
  promoteFrom?: string | null;
  segmentId?: number | null;
  manualOfferKind?: ManualOfferKind;
  discountAmount?: string;
}

export interface UpdateOfferResponse {
  offer: Offer;
}

export interface UpdateOfferParams {
  id: number;
  data: UpdateOfferRequest;
}

export interface DeleteOfferParams {
  id: number;
}

export interface DeleteOfferResponse {
  message?: string;
  error?: string;
}

export const PromotionStrategy = {
  ONSITE: 'onsite',
  SHOPPER_STRATEGIC: 'shopper_strategic',
} as const;
export type PromotionStrategy =
  typeof PromotionStrategy[keyof typeof PromotionStrategy];

// How a merchant brings an offer into Shopper from the entry-method step.
export const OfferEntryMethod = {
  SYNC: 'sync',
  MANUAL: 'manual',
} as const;
export type OfferEntryMethod =
  typeof OfferEntryMethod[keyof typeof OfferEntryMethod];

// The kind of offer chosen on the "What kind of offer?" radio in the manual
// entry path. Frontend-only for now; maps to the OMS offer proto later.
export const ManualOfferKind = {
  PERCENT_OFF: 'percent_off',
  AMOUNT_OFF: 'amount_off',
  FREE_SHIPPING: 'free_shipping',
  BXGY: 'bxgy',
  OTHER: 'other',
} as const;
export type ManualOfferKind =
  typeof ManualOfferKind[keyof typeof ManualOfferKind];

// Offer types presented on the unique-offer "Create Offer" selection screen.
// Frontend-only for now; each maps to an existing ManualOfferKind for the form.
export const CreateOfferType = {
  PRODUCT_DISCOUNT: 'product_discount',
  ORDER_DISCOUNT: 'order_discount',
  FREE_SHIPPING: 'free_shipping',
  BXGY: 'bxgy',
} as const;
export type CreateOfferType =
  typeof CreateOfferType[keyof typeof CreateOfferType];

// Whether the discount value is an exact amount/percent or a capped "up to".
export const DiscountValueQualifier = {
  EXACTLY: 'exactly',
  UP_TO: 'up_to',
} as const;
export type DiscountValueQualifier =
  typeof DiscountValueQualifier[keyof typeof DiscountValueQualifier];

export interface OfferFormValues {
  offerSource: OfferSource;
  offerType: OfferType;
  discountCode: string;
  discountValue: string;
  startDate: string;
  startTime: string;
  setEndDate: boolean;
  endDate: string;
  endTime: string;
  title: string;
  description: string;
  prePromotionControl: boolean; // UI-only: true = promote in advance, false = only when live
  promoteStartDate: string;
  promoteStartTime: string;
  allowStacking: boolean;
  discountRuleId?: number | null; // For unique discount codes
  segmentId?: number | null; // Optional segment ID for strategic offers
  // Manual-entry prototype fields (frontend-only for now; not yet persisted).
  // These describe the structured discount captured for % off / $ off offers
  // and map to the OMS offer proto later.
  manualOfferKind?: ManualOfferKind;
  discountValueQualifier?: DiscountValueQualifier;
  discountAmount?: string; // float held as a string for the numeric input
}

// shared props for all offer forms
export interface OfferFormProps {
  offerSource: OfferSource;
  offerType: OfferType;
  offer?: Offer | null;
  onSubmit: (values: OfferFormValues, isDraft: boolean) => Promise<void>;
  onDelete?: () => void;
  isLoading?: boolean;
  onDiscountModalOpenChange?: (isOpen: boolean) => void;
  promotionStrategy?: PromotionStrategy;
  manualOfferKind?: ManualOfferKind;
  disableDraft?: boolean;
}

// --- Offer Scanner review queue (frozen contract: reconciliation/openapi.yaml) ---

export const ProposalDisposition = {
  NEW: 'new',
  ENRICH: 'enrich',
  POSSIBLE_MATCH: 'possible_match',
  CONFLICTED: 'conflicted',
  DUPLICATE: 'duplicate',
  IGNORED: 'ignored',
  SUPPRESSED: 'suppressed',
} as const;

export type ProposalDisposition =
  typeof ProposalDisposition[keyof typeof ProposalDisposition];

export const ProposalStatus = {
  PENDING: 'pending',
  APPLIED: 'applied',
  REJECTED: 'rejected',
  FAILED: 'failed',
} as const;

export type ProposalStatus = typeof ProposalStatus[keyof typeof ProposalStatus];

export const ProposalTargetKind = {
  AI_OFFER: 'ai_offer',
  OMS_OFFER: 'oms_offer',
  NONE: 'none',
} as const;

export type ProposalTargetKind =
  typeof ProposalTargetKind[keyof typeof ProposalTargetKind];

export interface ProposalEvidence {
  sourceUrl?: string | null;
  quote?: string | null;
  observedAtUtc?: string | null;
}

export interface ProposalShopifyBacking {
  shopifyGid: string;
  title?: string | null;
  discountCode?: string | null;
}

export const LLM_CORRESPONDENCE_MATCH_RULE = 'llm_correspondence';

export interface ProposalApplicability {
  scope?: string | null;
  eligibility?: string | null;
  redemption?: string | null;
  timing?: string | null;
}

export interface ProposalConsolidation {
  applicability?: ProposalApplicability | null;
  reviewerEdited: boolean;
}

/**
 * 1: the stated code is an active Shopify discount of agreeing kind.
 * 2: theme copy or an active automatic discount corroborates it.
 * 3: prominent (announcement or hero) but uncorroborated.
 * null: page copy only, or never verified.
 */
export type ProposalVerificationTier = 1 | 2 | 3;

export const ProposalVerificationSource = {
  KNOWN_OFFER_CODE: 'known_offer_code',
  SHOPIFY_DISCOUNT_CODE: 'shopify_discount_code',
  THEME_SURFACE: 'theme_surface',
  SHOPIFY_AUTOMATIC_DISCOUNT: 'shopify_automatic_discount',
  PROMINENT_SURFACE: 'prominent_surface',
} as const;

export type ProposalVerificationSource =
  typeof ProposalVerificationSource[keyof typeof ProposalVerificationSource];

export const ProposalSurface = {
  ANNOUNCEMENT: 'announcement',
  HERO: 'hero',
  POPUP: 'popup',
  PAGE_BODY: 'page_body',
} as const;

export type ProposalSurface =
  typeof ProposalSurface[keyof typeof ProposalSurface];

export const ProposalDescriptionOrigin = {
  MODEL: 'model',
  REPAIRED: 'repaired',
  FACTS_ONLY: 'facts_only',
} as const;

export type ProposalDescriptionOrigin =
  typeof ProposalDescriptionOrigin[keyof typeof ProposalDescriptionOrigin];

export interface ProposalVerificationDiscount {
  gid: string;
  title: string;
  discountType: string;
  status: string;
  startsAt?: string | null;
  endsAt?: string | null;
}

export interface ProposalVerification {
  tier: ProposalVerificationTier | null;
  source: ProposalVerificationSource | null;
  detail: string | null;
  discount: ProposalVerificationDiscount | null;
  conflicts: string[];
  discoveryUnavailable: boolean;
  programmeKind: string | null;
  surface: ProposalSurface | null;
}

export interface OfferProposal {
  id: number;
  shopId: number;
  disposition: ProposalDisposition;
  status: ProposalStatus;
  updatedAt: string;
  appliedAt?: string | null;
  reviewAction?:
    | 'created_draft'
    | 'updated_existing'
    | 'linked_existing'
    | null;
  resultAiOfferId?: number | null;
  resultOmsOfferUuid?: string | null;
  targetKind: ProposalTargetKind;
  targetAiOfferId?: number | null;
  targetOmsOfferUuid?: string | null;
  matchRule?: string | null;
  matchScore: number;
  offerName: string;
  value: string;
  offerKind: string;
  canonicalKind: string;
  scopeAndEligibility?: string | null;
  redemption?: string | null;
  timing?: string | null;
  discountCode?: string | null;
  valuePercent?: string | null;
  valueAmountCents?: number | null;
  valueCurrency?: string | null;
  promotionKey?: string | null;
  duplicateCount: number;
  suggestedSitewide: boolean;
  currentSitewide?: boolean | null;
  sitewideRationale?: string | null;
  sitewideChangeRequested: boolean;
  valueConflict?: string | null;
  reviewable: boolean;
  stale: boolean;
  staleSince?: string | null;
  shopifyBacked?: boolean | null;
  shopifyBacking?: ProposalShopifyBacking | null;
  evidence: ProposalEvidence[];
  /** Model self-reported and not a confidence signal; read verificationTier. */
  scannerConfidence?: number;
  verificationTier?: ProposalVerificationTier | null;
  verification?: ProposalVerification | null;
  conditions?: string[] | null;
  /** Brand-voice prose, a blank line, then a block of dashed fact lines. */
  scannedDescription?: string | null;
  descriptionOrigin?: ProposalDescriptionOrigin | null;
  skipReason?: string | null;
  skipDetail?: string | null;
  consolidatedOfferName?: string | null;
  consolidatedDescription?: string | null;
  consolidation?: ProposalConsolidation | null;
  pagesSeenOn?: number | null;
}

export interface PromotionGroup {
  promotionKey: string;
  campaignLabel?: string | null;
  sightingCount: number;
  proposals: OfferProposal[];
}

export interface ProposalSummary {
  total: number;
  /** Highest proposal id in the filtered set; absent on older backends. */
  latestProposalId?: number | null;
  byDisposition: Record<string, number>;
  byStatus: Record<string, number>;
}

export interface ProposalListResponse {
  proposals: OfferProposal[];
  promotions?: PromotionGroup[] | null;
  totalCount: number;
  summary: ProposalSummary;
}

export const ProposalReviewScope = {
  THIS_SCAN: 'thisScan',
  ALL_PENDING: 'allPending',
} as const;

export type ProposalReviewScope =
  typeof ProposalReviewScope[keyof typeof ProposalReviewScope];

export interface OfferProposalScanStatus {
  scanning: boolean;
  statusAvailable?: boolean;
  lastCompletedAt?: string | null;
  lastCompletedRunId?: string | null;
}

export interface ProposalListParams {
  status?: ProposalStatus;
  disposition?: ProposalDisposition;
  runId?: string;
  observedInRun?: string;
  reviewable?: boolean;
  grouped?: boolean;
  limit?: number;
  offset?: number;
}

export interface LinkProposalRequest {
  targetAiOfferId?: number;
  targetOmsOfferUuid?: string;
  expectedProposalUpdatedAt?: string;
  expectedTargetUpdatedAt?: string;
}

export interface ProposalTargetPreviewField {
  label: string;
  current: string | null;
  after: string | null;
  changed: boolean;
}

export interface ProposalTargetPreview {
  proposalId: number;
  proposalUpdatedAt: string;
  targetKind: Exclude<ProposalTargetKind, 'none'>;
  targetAiOfferId: number | null;
  targetOmsOfferUuid: string | null;
  title: string;
  targetUpdatedAt: string | null;
  action: 'update_existing' | 'confirm_match';
  canApply: boolean;
  unavailableReason: string | null;
  fields: ProposalTargetPreviewField[];
}

export const ProposalEditableField = {
  OFFER_NAME: 'offerName',
  VALUE: 'value',
  SCOPE_AND_ELIGIBILITY: 'scopeAndEligibility',
  REDEMPTION: 'redemption',
  TIMING: 'timing',
} as const;

export type ProposalEditableField =
  typeof ProposalEditableField[keyof typeof ProposalEditableField];

/**
 * A merchant's corrections to what the scanner read, before they approve it.
 *
 * Only fields the merchant actually changed are sent -- the endpoint patches
 * literally, so an omitted field is left alone. `discountCode` is absent by
 * design: the code is matching identity server-side, so it is corrected on
 * the offer, never on the proposal.
 */
export type EditProposalRequest = Partial<
  Record<ProposalEditableField, string>
> & {
  description?: string;
  expectedUpdatedAt?: string;
};

/** Form state: every editable field, empty string standing in for null. */
export type ProposalEditFormValues = Record<ProposalEditableField, string> & {
  description: string;
};

/**
 * shop.data key naming the host the scanner should treat as the storefront.
 * For a headless store, Shopify's primary domain is the checkout host, not
 * where the merchant sells. The server honours this only within the shop's
 * own registrable domain.
 */
export const OFFER_SCANNER_STOREFRONT_DOMAIN_KEY =
  'offer_scanner_storefront_domain';
